import cx from "classnames";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { BasicButton } from "@gaming/xds-react-js";

import { useProgramBackgroundColorPicker } from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { OnboardingErrorType } from "../../models/errors";
import { getCurrentError, getIsSavingForm } from "../../redux/selectors";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { LoadingSpinner } from "../Spinner";
import * as styles from "./FormNavigationPanel.module.css";

export type FormNavigationPanelProps = Readonly<{
  onCancel?: () => void;
  onBackNavigation?: () => void;
  onSave: () => void;
  onForwardNavigation: () => void;
  onBeforeSave?: (proceed: () => void) => boolean;
}>;

export const FormNavigationPanel = ({
  onCancel,
  onBackNavigation,
  onSave,
  onForwardNavigation,
  onBeforeSave,
}: FormNavigationPanelProps) => {
  const [cancelString, backString, save, saveAndNextString, saveSubmitFormErrorMessage] =
    useStrings("cancel", "back", "save", "saveAndNext", "saveSubmitFormErrorMessage");
  const isSavingFormToCrm: boolean = useSelector(getIsSavingForm);
  const currentError = useSelector(getCurrentError);
  const backgroundColorStyle = useProgramBackgroundColorPicker(true);
  const [isSubFormInSaveState, setIsSubFormInSaveState] = useState(false);
  const [isSaveAndNextButtonClicked, setIsSaveAndNextButtonClicked] = useState(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);

  // Save & Next button post-click handling
  useEffect(() => {
    if (isSaveAndNextButtonClicked) {
      if (isSavingFormToCrm) {
        setIsSubFormInSaveState(true);
      } else {
        if (isSubFormInSaveState && currentError?.type !== OnboardingErrorType.Save) {
          onForwardNavigation();
        } else {
          setIsSubFormInSaveState(false);
        }
        setIsSaveAndNextButtonClicked(false);
      }
    }
  }, [
    isSavingFormToCrm,
    isSaveAndNextButtonClicked,
    currentError,
    isSubFormInSaveState,
    onForwardNavigation,
  ]);

  // Save button post-click handling
  useEffect(() => {
    if (isSaveButtonClicked) {
      if (isSavingFormToCrm) {
        setIsSubFormInSaveState(true);
      } else {
        setIsSubFormInSaveState(false);
        setIsSaveButtonClicked(false);
      }
    }
  }, [
    isSavingFormToCrm,
    isSaveButtonClicked,
    currentError,
    isSubFormInSaveState,
    onForwardNavigation,
  ]);

  if (isSubFormInSaveState) {
    return (
      <div className={cx(styles.navigationPanel, styles.spinnerWrapper)}>
        <LoadingSpinner />
      </div>
    );
  }

  const performSaveAndNext = () => {
    onSave();
    setIsSaveAndNextButtonClicked(true);
  };

  const onSaveAndNextButtonClick = () => {
    if (onBeforeSave) {
      const shouldProceed = onBeforeSave(performSaveAndNext);
      if (!shouldProceed) {
        return;
      }
    }
    performSaveAndNext();
  };

  const performSave = () => {
    onSave();
    setIsSaveButtonClicked(true);
  };

  const onSaveButtonClick = () => {
    if (onBeforeSave) {
      const shouldProceed = onBeforeSave(performSave);
      if (!shouldProceed) {
        return;
      }
    }
    performSave();
  };

  return (
    <div className={styles.navigationPanel}>
      {onCancel && (
        <BasicButton
          as={"button"}
          onClick={onCancel}
          className={commonStyles.secondaryButton}
          textDecorationUnderline={true}
        >
          {cancelString}
        </BasicButton>
      )}
      {onBackNavigation && (
        <BasicButton
          as={"button"}
          onClick={onBackNavigation}
          className={commonStyles.secondaryButton}
          textDecorationUnderline={true}
        >
          {backString}
        </BasicButton>
      )}
      <div className={styles.saveButtonWrapper}>
        <BasicButton
          as={"button"}
          onClick={onSaveButtonClick}
          className={commonStyles.secondaryButton}
          textDecorationUnderline={true}
        >
          {save}
        </BasicButton>
      </div>
      <div>
        <div className={styles.saveButtonWrapper}>
          <BasicButton
            as={"button"}
            onClick={onSaveAndNextButtonClick}
            className={cx(backgroundColorStyle, commonStyles.primaryButton)}
            textDecorationUnderline={true}
          >
            {saveAndNextString}
          </BasicButton>
        </div>
        {currentError?.type === OnboardingErrorType.Save && (
          <span className={styles.errorMessage}>{saveSubmitFormErrorMessage}</span>
        )}
      </div>
    </div>
  );
};
